import { useMemo, RefObject } from 'react';
import { DOCUMENT_ENRICHMENT_SECTIONS, useDocuments } from '@/hooks/useDocuments';
import { useCitationParsing } from '@/hooks/useCitationParsing';
import { useCitationTooltips } from '@/hooks/useCitationTooltips';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import {
  renderContentWithCitations,
  parseMarkdownSections,
  buildCitationNumberingMap,
} from '@/utils/citation-rendering';
import type { CitationInfo } from '@/utils/citation-utils';

const EMPTY_CITED_DOCUMENTS = new Map<string, CitationInfo[]>();

interface UseCaseOverviewContentParams {
  markdownContent: string | null;
  slug: string;
  scrollRef: RefObject<HTMLDivElement | null>;
}

export function useCaseOverviewContent({
  markdownContent,
  slug,
  scrollRef,
}: UseCaseOverviewContentParams) {
  const timezone = getTimezone();

  useCitationTooltips(slug, scrollRef);

  const { documents, fetchedDocuments } = useDocuments(
    slug,
    undefined,
    undefined,
    undefined,
    {},
    timezone,
    // Citations only need names and guids; this matches Case Partner's key so the two share one fetch.
    { excludeSections: DOCUMENT_ENRICHMENT_SECTIONS },
  );

  const { sources, citedDocuments: parsedCitedDocuments } = useCitationParsing(
    markdownContent,
    fetchedDocuments
  );

  // Until fetch-all resolves, parsing keeps every citation (undefined documents
  // are not treated as deleted). MatterMemory still receives `documents` as [],
  // so the Citations card would render empty. Hide the section until names and
  // deleted state can actually be resolved.
  const citedDocuments = fetchedDocuments === undefined
    ? EMPTY_CITED_DOCUMENTS
    : parsedCitedDocuments;

  const sections = useMemo(
    () => (markdownContent ? parseMarkdownSections(markdownContent) : []),
    [markdownContent]
  );

  const citationNumbers = useMemo(
    () => (markdownContent ? buildCitationNumberingMap(markdownContent) : undefined),
    [markdownContent]
  );

  const renderSectionHtml = (content: string) =>
    renderContentWithCitations({
      content,
      sources,
      documents: fetchedDocuments,
      slug,
      citationNumbers,
    });

  return {
    documents,
    citedDocuments,
    sections,
    renderSectionHtml,
  };
}
